import {
  BRANCH_HEADER,
  REQUEST_ID_HEADER,
  TERMINAL_SESSION_STATUSES,
  type MeasurementSessionStatus,
} from '@optiflow/types';
import { API_BASE_URL, refreshAccessTokenOnce } from '@/services/apiClient';
import { authStore } from '@/store/authStore';

/** What the stream carries: which session, where it stands, and the result's id once there is one. */
export interface SessionStatusEvent {
  sessionId: string;
  status: MeasurementSessionStatus;
  resultMeasurementId?: string;
}

/** Splits a server-sent event stream into events. A chunk can end mid-line, so the remainder is carried over. */
export class SseParser {
  private buffer = '';

  push(chunk: string): { event: string; data: string }[] {
    this.buffer += chunk.replace(/\r\n?/g, '\n');
    const events: { event: string; data: string }[] = [];

    let boundary = this.buffer.indexOf('\n\n');
    while (boundary !== -1) {
      const frame = this.buffer.slice(0, boundary);
      this.buffer = this.buffer.slice(boundary + 2);

      let event = 'message';
      const data: string[] = [];
      for (const line of frame.split('\n')) {
        // Blank lines end nothing here, and a leading colon is a keep-alive comment.
        if (line === '' || line.startsWith(':')) continue;
        const colon = line.indexOf(':');
        const field = colon === -1 ? line : line.slice(0, colon);
        const value = colon === -1 ? '' : line.slice(colon + 1).replace(/^ /, '');
        if (field === 'event') event = value;
        if (field === 'data') data.push(value);
      }
      if (data.length > 0) events.push({ event, data: data.join('\n') });

      boundary = this.buffer.indexOf('\n\n');
    }

    return events;
  }
}

export interface WatchOptions {
  onStatus: (event: SessionStatusEvent) => void;
  /** The stream could not be kept open; the caller should poll instead. */
  onFallback: () => void;
  signal: AbortSignal;
  backoffMs?: readonly number[];
  /** The pause before reopening a stream the server closed cleanly, so a misbehaving proxy cannot cause a tight loop. */
  reopenMs?: number;
  fetchImpl?: typeof fetch;
}

const BACKOFF_MS = [1_000, 2_000, 4_000, 8_000] as const;
const REOPEN_MS = 1_000;
const FAILURES_BEFORE_POLLING = 3;

/**
 * Follows one session over server-sent events until it ends.
 *
 * Read with fetch, so the staff Bearer header travels as a header — no token ever goes in a URL. A
 * stream the server closes while the session is still open (its token expired, or it reached a
 * deadline) is reopened straight away with fresh credentials. A 401 refreshes the token once
 * through the same single refresh the API client uses. After repeated failures the caller is told to
 * poll, because a desk that cannot see the phone is worse than a desk that looks a little later.
 */
export async function watchSession(sessionId: string, options: WatchOptions): Promise<void> {
  const fetchImpl = options.fetchImpl ?? fetch;
  const backoff = options.backoffMs ?? BACKOFF_MS;
  let failures = 0;
  let refreshedForThisFailure = false;

  while (!options.signal.aborted) {
    let heardSomething = false;

    try {
      const response = await fetchImpl(
        `${API_BASE_URL}/measurements/sessions/${sessionId}/events`,
        { headers: streamHeaders(), signal: options.signal, cache: 'no-store' },
      );

      if (response.status === 401 && !refreshedForThisFailure) {
        refreshedForThisFailure = true;
        await refreshAccessTokenOnce();
        continue;
      }
      if (!response.ok || !response.body)
        throw new Error(`The stream was refused (${response.status}).`);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      const parser = new SseParser();
      // Closing the dialog releases the stream even where an abort does not reach the response body.
      options.signal.addEventListener('abort', () => void reader.cancel().catch(() => undefined), {
        once: true,
      });

      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;

        for (const frame of parser.push(decoder.decode(value, { stream: true }))) {
          if (frame.event !== 'status') continue;

          const event = JSON.parse(frame.data) as SessionStatusEvent;
          heardSomething = true;
          failures = 0;
          refreshedForThisFailure = false;
          options.onStatus(event);
          if (TERMINAL_SESSION_STATUSES.includes(event.status)) return;
        }
      }

      // A healthy stream always opens with the current status; one that said nothing is a failure, not a pause.
      if (!heardSomething) throw new Error('The stream closed without a word.');
      await pause(options.reopenMs ?? REOPEN_MS, options.signal);
    } catch {
      if (options.signal.aborted) return;

      failures += 1;
      if (failures >= FAILURES_BEFORE_POLLING) {
        options.onFallback();
        return;
      }
      await pause(backoff[Math.min(failures - 1, backoff.length - 1)] ?? 0, options.signal);
    }
  }
}

function streamHeaders(): Record<string, string> {
  const { accessToken, activeBranchId } = authStore.get();
  return {
    Accept: 'text/event-stream',
    [REQUEST_ID_HEADER]: crypto.randomUUID(),
    ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    ...(activeBranchId ? { [BRANCH_HEADER]: activeBranchId } : {}),
  };
}

function pause(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    const timer = setTimeout(resolve, ms);
    signal.addEventListener(
      'abort',
      () => {
        clearTimeout(timer);
        resolve();
      },
      { once: true },
    );
  });
}
